import React, { FunctionComponent, MouseEvent, ReactNode } from 'react';
import {
	CardContainerContext,
	ICardContainerContextValues,
} from './CardContainerContext';
import { StyledCardPicture } from './StyledCardPicture';
import { StyledCardContent } from './StyledCardContent';
import { StyledCardChildren } from './StyledCardChildren';
import { StyledCardLink } from './StyledCardLink';
import { IResponsiveImageSource } from '../Image';
import { isRelativeUrl } from '../Link';
import { StyledCard } from './StyledCard';
import { useOnClickCardHandler } from './useOnClickCardHandler';
import { StyledCardTitle } from './StyledCardTitle';
import { HeaderLevel } from '../Headings';

export interface ICardProps {
	children?: ReactNode;
	title: string;
	screenReaderLinkTitle?: string;
	linkTitle?: string;
	linkHref: string;
	imageSource: IResponsiveImageSource;
	imageAlt?: string;
	className?: string;
	onClick?: (e: MouseEvent<HTMLLIElement>) => void;
	titleHeaderLevel?: HeaderLevel;
	fullyClickable?: boolean;
}

export const withCardContainerContext = <T extends {} = {}>(
	Component: FunctionComponent<T & ICardContainerContextValues>,
) =>
	function (props: T) {
		return (
			<CardContainerContext.Consumer>
				{(contextValues: ICardContainerContextValues) => (
					<Component {...contextValues} {...props} />
				)}
			</CardContainerContext.Consumer>
		);
	};

export const Card = withCardContainerContext<ICardProps>(
	({
		children,
		title,
		linkTitle,
		screenReaderLinkTitle,
		linkHref,
		imageSource,
		imageAlt,
		fullyClickable,
		onClick,
		titleHeaderLevel,
		...cardProps
	}) => {
		const onClickUrl = fullyClickable ? linkHref : undefined;
		const defaultOnClick = useOnClickCardHandler(onClickUrl);
		const finalLinkTitle = linkTitle === undefined ? 'Read more' : linkTitle;

		return (
			<StyledCard
				fullyClickable={fullyClickable}
				onClick={onClick ?? defaultOnClick}
				{...cardProps}
			>
				<StyledCardPicture source={imageSource} alt={imageAlt} />
				<StyledCardContent>
					<div>
						<StyledCardTitle styling={titleHeaderLevel || 'h3'}>
							{title}
						</StyledCardTitle>
						<StyledCardChildren>{children}</StyledCardChildren>
					</div>
					<div>
						<StyledCardLink
							href={linkHref}
							screenReaderTitle={screenReaderLinkTitle}
							target={!isRelativeUrl(linkHref) ? '_blank' : undefined}
						>
							{finalLinkTitle}
						</StyledCardLink>
					</div>
				</StyledCardContent>
			</StyledCard>
		);
	},
);
